import { memo, useEffect, useState } from 'react'
import { EnhancedPaper, showNotification } from 'summon-ui'
import { useTranslation } from 'summon-ui/intl'
import { Button, Divider, Group, Modal, Title, Text, Stack, Box } from 'summon-ui/mantine'
import { useBlockNumber, useReadContract } from 'wagmi'
import { useQueryClient } from '@tanstack/react-query'
import { graphqlClient } from '@api/config'
import { BridgeLink } from '@components'
import { TENANT_CONFIG, TENANT_CONTRACTS, TENANT_ONBOARDING } from '@config'
import { AVATAR_BOUND_ABI } from '@constants/Abi/avatarBoundAbi'
import AvatarWithInventory from '@features/onboarding/components/AvatarWithInventory'
import {
  useEnsureLevel0ClaimableItemExistMutation,
  useMarkClaimableItemAsUsedMutation,
  useSaveAvatarMutation
} from '@generated/generates'
import withWaitingRoom from '@hocs/withWaitingRoom'
import { useAddOnchainHistoryLog, useTokenGating, useViewer, useMintTypeBased } from '@hooks'
import { MintType } from '@hooks/useMintTypeBased'
import { isStaging } from '@utils/isStaging'
import OnboardingHeader from '../components/OnboardingHeader'
import { useOnboardingStorage } from '../onboarding.store'

const CONFIRM_MODAL_DESCRIPTION =
  'By minting you will receive a soulbound avatar, as well as several equippable NFT items.'

const MintAvatarOnchain = () => {
  const t = useTranslation()
  const queryClient = useQueryClient()
  const { viewer } = useViewer()
  const { nextStep, previousStep, selectedOnboardingSkin } = useOnboardingStorage()
  const { closedCapsuleId } = useTokenGating()
  const [isMainnet, setIsMainnet] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [nonce, setNonce] = useState<null | undefined | number>(null)
  const [signature, setSignature] = useState<null | undefined | string>(null)
  const [randomDataString, setRandomDataString] = useState<null | undefined | string>(null)
  const [initialClaimableId, setInitialClaimableId] = useState<string | null>(null)
  const [isMintModalOpen, setIsMintModalOpen] = useState(false)
  const [mintType, setMintType] = useState(MintType.Eth)
  const refreshUserQuery = () => queryClient.invalidateQueries({ queryKey: ['GetUser'] })

  const { addOnchainHistoryLog } = useAddOnchainHistoryLog()
  const { mutateAsync: saveAvatar } = useSaveAvatarMutation(graphqlClient(), {})
  // We need to mark as completed the first level claimable after mint the avatar
  const { mutateAsync: markAsClaimed } = useMarkClaimableItemAsUsedMutation(graphqlClient(), {
    onSuccess() {
      setIsMintModalOpen(false)
      refreshUserQuery()
      nextStep()
    }
  })

  // Get the data needed for mint avatar + id to mark the first claimable as used after minting
  const { mutateAsync: getDataToPrepareMint } = useEnsureLevel0ClaimableItemExistMutation(graphqlClient(), {
    onSuccess(data) {
      const { id, nonce, randomData, signature } = data.ensureLevel0ClaimableItemExist
      setInitialClaimableId(id)
      setNonce(nonce)
      setSignature(signature)
      setIsMainnet(data.ensureLevel0ClaimableItemExist?.data?.isMainnet)
      setRandomDataString(randomData)
    },
    onError(error) {
      console.error('Error preparing mint: ', error)
    }
  })

  const nftGatingId = TENANT_CONFIG.features.tokenGating ? [Number(closedCapsuleId)] : []
  const baseSkinId = Number(selectedOnboardingSkin?.id)
  const { mint, errorMessage } = useMintTypeBased({
    mintType,
    functionName: TENANT_CONFIG.features.tokenGating ? 'mintAvatarNftGating' : 'mintAvatar',
    args: [...nftGatingId, baseSkinId, nonce, randomDataString, signature],
    contract: isMainnet ? TENANT_CONTRACTS.mainnet.avatar : TENANT_CONTRACTS.testnet.avatar,
    abi: AVATAR_BOUND_ABI,
    isMainnet
  })

  const { data: tokenIdData, refetch } = useReadContract({
    address: isStaging ? TENANT_CONTRACTS.testnet.avatar : TENANT_CONTRACTS.mainnet.avatar,
    abi: AVATAR_BOUND_ABI,
    chainId: TENANT_CONFIG.network.id,
    functionName: 'tokenOfOwnerByIndex',
    args: [viewer?.walletAddress, 0],
    query: { enabled: !!viewer?.walletAddress }
  })
  const { data: blockNumber } = useBlockNumber({ watch: !tokenIdData })

  useEffect(() => {
    if (!tokenIdData) {
      refetch()
    } else {
      const saveAndMarkAsClaimed = async () => {
        if (tokenIdData && initialClaimableId) {
          await saveAvatar({ tokenId: Number(tokenIdData) as number, skinId: selectedOnboardingSkin?.glbUrl as string })
          await markAsClaimed({ markClaimableItemAsUsedId: initialClaimableId as string })
        }
      }
      saveAndMarkAsClaimed()
    }
  }, [blockNumber, tokenIdData])

  useEffect(() => {
    if (viewer?.walletAddress) {
      getDataToPrepareMint({})
    }
  }, [viewer?.walletAddress])

  useEffect(() => {
    if (errorMessage) {
      setIsMintModalOpen(false)
    }
  }, [errorMessage])

  const finishMint = async () => {
    try {
      setIsLoading(true)
      const tx = await mint()

      await addOnchainHistoryLog({
        txAddress: tx.transactionHash,
        contractAddress: tx.to,
        description: 'Avatar minted'
      })
    } catch (error) {
      setIsLoading(false)
      showNotification({
        variant: 'danger',
        message: 'Could not mint the Avatar'
      })
    }
  }

  const handleMint = () => {
    setIsMintModalOpen(true)
  }

  return (
    <>
      <Box mah={100}>
        <OnboardingHeader title={TENANT_ONBOARDING.avatar.title} subTitle={TENANT_ONBOARDING.avatar.subtitle} />
        <Box h='100%' pl='sm' pb='70'>
          <AvatarWithInventory isDNA={true} />
        </Box>

        <EnhancedPaper withBg w='100%' className='md:absolute fixed bottom-0 left-0'>
          <Divider mb='md' />
          <Group justify='space-between' px='lg' mb='lg'>
            <Button variant='outline' miw={100} onClick={previousStep}>
              {t('Back')}
            </Button>
            <Button onClick={handleMint} miw={100}>
              {t('Mint')}
            </Button>
          </Group>
        </EnhancedPaper>
      </Box>

      <Modal opened={isMintModalOpen} onClose={() => setIsMintModalOpen(false)} maw={400} radius='md'>
        <ConfirmModal
          disabled={false}
          setMintType={setMintType}
          mintType={mintType}
          isLoading={isLoading}
          action={finishMint}
          close={() => setIsMintModalOpen(false)}
        />
      </Modal>
    </>
  )
}

const ConfirmModal = memo(
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  withWaitingRoom(({ close, isLoading, disabled, nextStep }: any) => {
    const t = useTranslation()

    return (
      <Stack gap='xs' p='sm'>
        <Title order={3}>{t("You're about to Mint!")}</Title>
        <Text>{t(CONFIRM_MODAL_DESCRIPTION)}.</Text>
        <BridgeLink />

        <Group justify='space-between' mt='md'>
          <Button variant='outline' onClick={close}>
            {t('Cancel')}
          </Button>
          <Button onClick={nextStep} disabled={disabled || isLoading} loading={isLoading}>
            {t('Continue')}
          </Button>
        </Group>
      </Stack>
    )
  })
)

export default MintAvatarOnchain
